// Compact, versioned codec for large URL-param values — today just the
// sidebar tree cache mirrored to ?ui= (see App.jsx). That blob grows with
// every scope the user expands, and JSON percent-encoded into a query string
// balloons (each `"` → %22, each `{` → %7B). We compress it instead.
//
// Format: a single-char SCHEME prefix followed by the payload, so we can
// evolve the encoding and still parse links shared under an older one:
//
//   "1" + compressToEncodedURIComponent(json)   ← current scheme
//   anything else                               ← legacy: the value was plain
//                                                 JSON that App.jsx wrote with
//                                                 encodeURIComponent(); older
//                                                 links double-encoded it.
//
// lz-string's compressToEncodedURIComponent emits only [A-Za-z0-9+-$], all of
// which survive URLSearchParams round-tripping (`+`/`$` ride through as
// %2B/%24 and decode back). The codec is intentionally synchronous so the
// mount-time read in App.jsx stays a single pass with no async barrier.

import LZString from "lz-string";

const SCHEME = "1";

// Serialize a JSON-able value into a compact, URL-safe param string.
export function encodeUrlState(value) {
    return SCHEME + LZString.compressToEncodedURIComponent(JSON.stringify(value));
}

// Inverse of encodeUrlState. Accepts both the current compressed scheme and
// legacy plain-JSON values (single- or double-encoded). Returns null on an
// empty/corrupt value so callers can fall back to a default.
export function decodeUrlState(raw) {
    if (!raw) return null;
    let json;
    if (raw[0] === SCHEME) {
        json = LZString.decompressFromEncodedURIComponent(raw.slice(1));
    } else {
        // Legacy link. URLSearchParams.get() already peeled one percent
        // layer; if what's left still isn't JSON it was double-encoded, so
        // peel once more.
        json = raw;
        try {
            JSON.parse(json);
        } catch {
            try {
                json = decodeURIComponent(raw);
            } catch {
                return null;
            }
        }
    }
    if (!json) return null;
    try {
        return JSON.parse(json);
    } catch {
        return null;
    }
}
